<script lang="ts">
	import { getNumBackInUnits, sanitizeInputs, toBN } from '$lib/util/util';
	import { fly } from 'svelte/transition';
	import BigNumber from 'bignumber.js';
	import { onDestroy, onMount } from 'svelte';
	import { DEFAULT_TAKE_PROFIT_GAIN_PERCENTAGE } from '$lib/home/constants.js';
	import {
		positionData,
		takeProfitInput,
		isTakeProfitInputValid,
		estimatedProfit,
		gainPercentage,
		tpslPostTradeDetailsLoading
	} from '../popupsStore';
	import { marketPrices } from '$lib/util/marketHelper';
	import { marketsConfig } from '$lib/config/markets';
	import { markets } from '$lib/home/store';
	import ProcessingIcon from '$lib/util/ProcessingIcon.svelte';
	import type { Market } from '$lib/types/market';

	let _takeProfitInput: string;
	let _gainPercentage: string = DEFAULT_TAKE_PROFIT_GAIN_PERCENTAGE;
	let currentMarket = $markets.find(
		(market) => market.marketIdentifier === $positionData.marketIdentifier
	) as Market;
	let currentOraclePrice: string;

	$: currentOraclePrice = $positionData.asset ? currentMarket.safePrice : '0';

	const isLong = new BigNumber($positionData.size).isPositive();

	let gainPercentageEventListner = () => {};
	let takeProfitEventListner = () => {};

	onMount(() => {
		if (
			new BigNumber($positionData.takeProfit).gt(0) ||
			new BigNumber($positionData.stopLoss).gt(0)
		) {
			_takeProfitInput = getNumBackInUnits($positionData.takeProfit, 18, 4);
			handleTakeProfitInput();
		} else {
			_gainPercentage = DEFAULT_TAKE_PROFIT_GAIN_PERCENTAGE;
			gainPercentage.set(_gainPercentage);
			handleGainPercentageInput();
		}
		gainPercentageEventListner = marketPrices.subscribe((val) => {
			if (val) {
				syncGainPercentageValue();
			}
		});
	});

	const syncGainPercentageValue = () => {
		_gainPercentage =
			currentOraclePrice && new BigNumber(_takeProfitInput).isGreaterThan(0)
				? isLong
					? new BigNumber(_takeProfitInput)
							.multipliedBy(1e18)
							.minus(currentOraclePrice)
							.div(currentOraclePrice)
							.multipliedBy(100)
							.toFixed(4)
							.toString()
					: new BigNumber(currentOraclePrice)
							.minus(new BigNumber(_takeProfitInput).multipliedBy(1e18))
							.div(currentOraclePrice)
							.multipliedBy(100)
							.toFixed(4)
							.toString()
				: '';
		gainPercentage.set(_gainPercentage);
	};

	const syncTakeProfitValue = () => {
		_takeProfitInput = _gainPercentage
			? isLong
				? new BigNumber(1)
						.plus(new BigNumber(_gainPercentage).div(100))
						.multipliedBy(currentOraclePrice)
						.div(1e18)
						.toFixed(marketsConfig[$positionData.asset].showDecimals + 2)
						.toString()
				: new BigNumber(1)
						.minus(new BigNumber(_gainPercentage).div(100))
						.multipliedBy(currentOraclePrice)
						.div(1e18)
						.toFixed(marketsConfig[$positionData.asset].showDecimals + 2)
						.toString()
			: '';
		takeProfitInput.set(toBN(_takeProfitInput || '0').toString());
	};

	const handleTakeProfitInput = () => {
		_takeProfitInput = sanitizeInputs(_takeProfitInput);
		takeProfitInput.set(toBN(_takeProfitInput || '0').toString());
		syncGainPercentageValue();
	};

	const handleGainPercentageInput = () => {
		_gainPercentage = sanitizeInputs(_gainPercentage);
		gainPercentage.set(_gainPercentage);
		syncTakeProfitValue();
	};

	const onGainInputFocus = () => {
		gainPercentageEventListner();
		takeProfitEventListner();
		takeProfitEventListner = marketPrices.subscribe((val) => {
			if (val) {
				syncTakeProfitValue();
			}
		});
	};

	const onTakeProfitInputFocus = () => {
		takeProfitEventListner();
		gainPercentageEventListner();
		gainPercentageEventListner = marketPrices.subscribe((val) => {
			if (val) {
				syncGainPercentageValue();
			}
		});
	};

	onDestroy(() => {
		takeProfitInput.set('0');
		gainPercentage.set('0');
		gainPercentageEventListner();
		takeProfitEventListner();
	});

	const removeTakeProfit = () => {
		takeProfitInput.set('0');
		_gainPercentage = '';
		gainPercentage.set(_gainPercentage);
		handleGainPercentageInput();
	};

	let estimatedProfitValue = '';
	let isProfitPositive = true;

	$: if (parseFloat($estimatedProfit) >= 0) {
		estimatedProfitValue = '+$' + Math.abs(parseFloat($estimatedProfit)).toFixed(2);
		isProfitPositive = true;
	} else {
		estimatedProfitValue = '-$' + Math.abs(parseFloat($estimatedProfit)).toFixed(2);
		isProfitPositive = false;
	}

	let priceOfAsset = '0';
	$: {
		const targetMarket = $markets.find((m) => m.marketIdentifier == $positionData.marketIdentifier);
		priceOfAsset = targetMarket?.safePrice || '0';
	}
</script>

<div class="flex justify-between items-center">
	<div class="font-medium text-fx-grey-200 text-md mt-2">Take Profit</div>
	{#if _gainPercentage !== ''}
		<button
			class="text-fx-blue text-sm cursor-pointer underline"
			on:click={removeTakeProfit}
			on:keydown={removeTakeProfit}
		>
			remove
		</button>
	{/if}
</div>
<div class="flex gap-2 mt-2">
	<div
		class="bg-fx-grey-800 p-2 rounded-md flex-1 flex justify-between items-center"
		class:border={!$isTakeProfitInputValid}
		class:border-fx-red={!$isTakeProfitInputValid}
	>
		<div class="flex-1 flex">
			<span class="text-xs">$</span>
			<input
				inputmode="decimal"
				autocomplete="off"
				autocorrect="off"
				pattern="^[0-9]*[.,]?[0-9]*$"
				minlength="1"
				maxlength="79"
				spellcheck="false"
				type="string"
				placeholder="0"
				data-testid="open-position-amount"
				class="bg-transparent text-fx-white font-medium outline-none text-xs w-full"
				bind:value={_takeProfitInput}
				on:input={handleTakeProfitInput}
				on:focus={onTakeProfitInputFocus}
			/>
		</div>

		<div class="text-xs text-fx-grey-400"><span class="mr-2">|</span> Take Profit</div>
	</div>
	<div
		class="bg-fx-grey-800 p-2 rounded-md flex-1 flex justify-between items-center"
		class:border={!$isTakeProfitInputValid}
		class:border-fx-red={!$isTakeProfitInputValid}
	>
		<div class="flex-1">
			<input
				inputmode="decimal"
				autocomplete="off"
				autocorrect="off"
				pattern="^[0-9]*[.,]?[0-9]*$"
				minlength="1"
				maxlength="79"
				spellcheck="false"
				type="string"
				placeholder="0"
				data-testid="open-position-amount"
				class="bg-transparent text-fx-white font-medium outline-none text-xs w-full"
				bind:value={_gainPercentage}
				on:input={handleGainPercentageInput}
				on:focus={onGainInputFocus}
			/>
		</div>

		<div class="text-xs text-fx-grey-400"><span class="mr-2">|</span> Gain %</div>
	</div>
</div>
{#if !$isTakeProfitInputValid}
	<div
		class="text-fx-red text-xxs mt-1 absolute font-semibold"
		transition:fly={{ y: -10, duration: 200 }}
	>
		{new BigNumber($positionData.size).gt(0)
			? `Min TP: ${getNumBackInUnits(
					new BigNumber(priceOfAsset).toString(),
					18,
					marketsConfig[$positionData.asset].showDecimals
			  )}`
			: `Max TP: ${getNumBackInUnits(
					new BigNumber(priceOfAsset).toString(),
					18,
					marketsConfig[$positionData.asset].showDecimals
			  )}`}
	</div>
{/if}
<div class="flex justify-between mt-4">
	<div class="text-fx-grey-400 text-xs font-medium">
		{isProfitPositive ? 'Est. Profit' : 'Est. Loss'}
	</div>
	<div class="flex gap-2">
		{#if $tpslPostTradeDetailsLoading}
			<ProcessingIcon />
		{/if}
		{#if $isTakeProfitInputValid}
			<div
				class="text-xs font-medium {isProfitPositive
					? 'text-fx-gradient-23'
					: 'text-fx-gradient-22'}"
			>
				{estimatedProfitValue}
			</div>
		{:else}
			<div class="text-fx-gradient-23 text-xs font-medium">$0.00</div>
		{/if}
	</div>
</div>
